import { HttpParams } from '@angular/common/http';
import { Component, forwardRef, Inject, Input, QueryList, ViewChild, ViewChildren, ViewContainerRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Algorithm, AlgorithmOutput, WorkSessionDefinition } from '../../../model';
import { AlgorithmService } from '../../../service/algorithm.service';
import { AppService } from '../../../service/app.service';
import { FieldService } from '../../../service/field.service';
import { WorkSessionDefinitionService } from '../../../service/work-session-definition.service';
import { WorkSessionAdvancedSearchAddPropertiesDialog } from '../../advanced-search/work-session-advanced-search/work-session-advanced-search-add-properties-dialog.component';
import { WorkSessionAdvancedSearchComponent } from '../../advanced-search/work-session-advanced-search/work-session-advanced-search.component';
import { LocalizationPipe } from '../../pipe';
import { AbstractSearchFieldComponent } from '../abstract-search-field.component';
import { SearchTargetType } from '../search-field.component';
import { SearchFieldService } from '../search-field.service';
import { WorkSessionsPropertySearchInputComponent } from './work-sessions-property-search-input.component';

@Component({
    selector: 'work-sessions-search-field',
    template: require('./work-sessions-search-field.component.html'),
    styles: [require('../search-field.component.css')],
})
export class WorkSessionsSearchFieldComponent extends AbstractSearchFieldComponent {

    @Input() isHistorical: boolean;

    @Input() periodFilterEnabled: boolean = true;

    @ViewChild(WorkSessionAdvancedSearchComponent) workSessionAdvancedSearchComponent: WorkSessionAdvancedSearchComponent;

    @ViewChild(WorkSessionAdvancedSearchAddPropertiesDialog) addPropertiesDialog: WorkSessionAdvancedSearchAddPropertiesDialog;

    @ViewChildren(WorkSessionsPropertySearchInputComponent) private workSessionsPropertySearchInputComponent: QueryList<WorkSessionsPropertySearchInputComponent>;

    private algorithmByIds: { [id: string]: Algorithm };

    postComputationResultFieldOutputs: { resultField: string, algorithmOutputs: AlgorithmOutput[] }[];
    loaded: boolean;

    constructor(
        @Inject(forwardRef(() => FieldService)) fieldService: FieldService,
        @Inject(forwardRef(() => SearchFieldService)) searchFieldService: SearchFieldService,
        @Inject(forwardRef(() => MatDialog)) matDialog: MatDialog,
        @Inject(forwardRef(() => ViewContainerRef)) vcRef: ViewContainerRef,
        @Inject(forwardRef(() => AppService)) appService: AppService,
        @Inject(forwardRef(() => WorkSessionDefinitionService)) private workSessionDefinitionService: WorkSessionDefinitionService,
        @Inject(forwardRef(() => AlgorithmService)) private algorithmService: AlgorithmService,
        @Inject(forwardRef(() => LocalizationPipe)) localizationPipe: LocalizationPipe
    ) {
        super(fieldService, searchFieldService, matDialog, vcRef, appService, localizationPipe);
        this.getPostComputationAlgorithmOutputs().then(outputs => {
            this.postComputationResultFieldOutputs = outputs;
            this.loaded = true;
        }).catch(() => {
            this.postComputationResultFieldOutputs = [];
            this.loaded = true;
        });
        this.searchTarget = this.isHistorical ? SearchTargetType.HISTORICAL_WORK_SESSIONS : SearchTargetType.ACTIVE_WORK_SESSIONS;
    }

    openAddPropertiesDialog(advancedSearchProperties: { name: string, label: string }[]): void {
        this.close();
        this.advancedSearchProperties = advancedSearchProperties;
        setTimeout(() => this.addPropertiesDialog.open(), 400);
    }

    closeAddPropertiesDialog(updatedSearchFields: string[]): void {
        if (updatedSearchFields) {
            this.workSessionAdvancedSearchComponent.updateSearchFields(updatedSearchFields);
        }
        setTimeout(() => this.open(), 400);
    }

    performClear(): void {
        this.workSessionAdvancedSearchComponent?.clearAll();
        this.clearInputs();
    }

    protected updateAdvancedSearch(fieldsToSaveBody: any): void {
        this.workSessionAdvancedSearchComponent?.updateLocalStorage(fieldsToSaveBody);
        this.workSessionAdvancedSearchComponent?.initAllConfigurations();
    }

    protected getEncodedRawValues(): void {
        let body: any = {};
        this.workSessionsPropertySearchInputComponent?.forEach(property => Object.assign(body, property.getFormValue(true)));
        return body;
    }

    protected getRawValues() {
        let body: any = {};
        this.workSessionsPropertySearchInputComponent?.forEach(property => Object.assign(body, property.getFormValue(false)));
        return body;
    }

    protected refreshInputConfigurations(): void {
        this.workSessionsPropertySearchInputComponent.forEach(input => {
            input.refreshConfiguration();
        });
    }

    private getPostComputationAlgorithmOutputs(): Promise<{ resultField: string, algorithmOutputs: AlgorithmOutput[] }[]> {
        const params = new HttpParams().set("postComputationOnly", true);
        return this.workSessionDefinitionService.getRecursivelyAllWorkSessionDefinitions(null, null, params).then(wsDefinitions => {
            this.algorithmByIds = {};
            const algorithmToFind = this.getPostComputationAlgorithmIds(wsDefinitions);
            const promises = algorithmToFind.map(id =>
                this.algorithmService.getAlgorithmById(id)
                    .then(algorithm => this.algorithmByIds[id] = algorithm)
                    .catch(() => null)
            );
            return Promise.all(promises).then(() => {
                let results = [];
                wsDefinitions.forEach(def => {
                    def.postComputationAlgorithms.forEach(postComp => {
                        if (this.algorithmByIds[postComp.algorithmId].outputs?.length && !results.some(postComputationAlgorithm => postComp.resultField == postComputationAlgorithm.resultField)) {
                            results.push({ resultField: postComp.resultField, algorithmOutputs: this.algorithmByIds[postComp.algorithmId].outputs });
                        }
                    });
                });
                return results;
            });
        });
    }

    private getPostComputationAlgorithmIds(wsDefinitions: WorkSessionDefinition[]): string[] {
        const results = [];
        wsDefinitions?.forEach(def => {
            def.postComputationAlgorithms?.forEach(alg => {
                if (!results.includes(alg.algorithmId)) {
                    results.push(alg.algorithmId);
                }
            });
        });
        return results;
    }

}